import type {RepositoryMigratingNotice as RepositoryMigratingNoticePayload} from '@github-ui/code-view-types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner, Link} from '@primer/react'
import {useState} from 'react'

import styles from './RepositoryMigratingNotice.module.css'

interface RepositoryMigratingNoticeProps {
  migratingNotice?: RepositoryMigratingNoticePayload
  ownerLogin: string
  returnFocusIds?: string[]
}

// Move focus to the first candidate id that is currently visible.
function restoreFocusAfterDismiss(returnFocusIds?: string[]) {
  if (!returnFocusIds) return

  let target: HTMLElement | null = null

  for (const id of returnFocusIds) {
    const candidate = document.getElementById(id)
    if (candidate?.checkVisibility()) {
      target = candidate
      break
    }
  }

  target?.focus()
}

export function RepositoryMigratingNotice({
  migratingNotice,
  ownerLogin,
  returnFocusIds,
}: RepositoryMigratingNoticeProps) {
  if (!migratingNotice) return null

  switch (migratingNotice.kind) {
    case 'in_progress':
      return <InProgressNotice />
    case 'exported':
      return <ExportedNotice exportedToUrl={migratingNotice.exportedToUrl} ownerLogin={ownerLogin} />
    case 'trade_controls':
      return (
        <TradeControlsNotice
          messageHtml={migratingNotice.messageHtml}
          ctaHtml={migratingNotice.ctaHtml}
          dismissPath={migratingNotice.dismissPath}
          returnFocusIds={returnFocusIds}
        />
      )
  }
}

function InProgressNotice() {
  return (
    <Banner variant="warning" title="Repository migration notice" hideTitle flush>
      <Banner.Description>
        This repository is currently being migrated. It&apos;s locked while the migration is in progress.
      </Banner.Description>
    </Banner>
  )
}

function ExportedNotice({exportedToUrl, ownerLogin}: {exportedToUrl: string; ownerLogin: string}) {
  return (
    <Banner variant="warning" title="Repository migration notice" hideTitle flush>
      <Banner.Description>
        This repository has been migrated to another deployment of GitHub. You can now access it at{' '}
        <strong>
          <Link inline href={exportedToUrl}>
            {exportedToUrl}
          </Link>
        </strong>
        . Please contact the repository&apos;s owner,{' '}
        <strong>
          <Link inline href={`/${ownerLogin}`}>
            {ownerLogin}
          </Link>
        </strong>
        , for more information.
      </Banner.Description>
    </Banner>
  )
}

interface TradeControlsNoticeProps {
  messageHtml: SafeHTMLString
  ctaHtml: SafeHTMLString
  dismissPath: string
  returnFocusIds?: string[]
}

function TradeControlsNotice({messageHtml, ctaHtml, dismissPath, returnFocusIds}: TradeControlsNoticeProps) {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  const postDismissal = async () => {
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      return
    }
  }

  const dismissTradeControls = () => {
    restoreFocusAfterDismiss(returnFocusIds)
    setDismissed(true)
    void postDismissal()
  }

  return (
    <Banner variant="warning" title="Trade controls notice" hideTitle flush onDismiss={dismissTradeControls}>
      <Banner.Description className={styles.BannerDescription}>
        <VerifiedHTMLDiv html={messageHtml} />
        <VerifiedHTMLDiv html={ctaHtml} />
      </Banner.Description>
    </Banner>
  )
}
